import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressDstoreApiBackupV2BackupServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
      {
        srcPath: '/_api/data/v2/backups',
        destPath: '/v2/backups',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
      {
        srcPath: '/_api/data/v2/backups',
        destPath: '/v2/backups',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
      {
        srcPath: '/_api/data/v2/backups',
        destPath: '/v2/backups',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
      {
        srcPath: '/_api/data/v2/backups',
        destPath: '/v2/backups',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
      {
        srcPath: '/_api/data/v2/backups',
        destPath: '/v2/backups',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/wix-data/v1/backups',
        destPath: '/api/v1/backups',
      },
      {
        srcPath: '/wix-data/v2/backups',
        destPath: '/v2/backups',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/data/v2/backups',
        destPath: '/v2/backups',
      },
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
    ],
    _: [
      {
        srcPath: '/data/v2/backups',
        destPath: '/v2/backups',
      },
      {
        srcPath: '/_api/data/backups',
        destPath: '/api/v1/backups',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_data_backups';

/**
 * Creates an on-demand backup of live content in a site's collections.
 *
 *
 * By default, all of the site's collections are included in the backup. For a partial backup, specify which collections to include in the `backup.collections` parameter.
 *
 * The process of creating a backup takes time.
 * You can check whether a backup has completed successfully with List Backups.
 *
 * You can store up to 3 on-demand backups for each site.
 * If 3 on-demand backups already exist, the oldest existing on-demand backup for the site is deleted when a new one is created. Automated backups are not affected.
 */
export function createBackup(payload: object): RequestOptionsFactory<any> {
  function __createBackup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v2.backup',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.dstore.api.backup.v2.BackupService.CreateBackup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressDstoreApiBackupV2BackupServiceUrl({
        protoPath: '/v2/backups',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'backup.requestedDate' },
              { path: 'backup.startedDate' },
              { path: 'backup.finishedDate' },
              { path: 'backup.deletedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createBackup;
}

/**
 * Retrieves a list of all backups for a site.
 *
 * Results are sorted by requested date, with the newest first.
 *
 * You can use this method to check whether a backup initiated with Create Backup has been completed successfully.
 */
export function listBackups(payload: object): RequestOptionsFactory<any> {
  function __listBackups({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v2.backup',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.dstore.api.backup.v2.BackupService.ListBackups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressDstoreApiBackupV2BackupServiceUrl({
        protoPath: '/v2/backups',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'backups.requestedDate' },
              { path: 'backups.startedDate' },
              { path: 'backups.finishedDate' },
              { path: 'backups.deletedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listBackups;
}

/**
 * Restores all data from a backup.
 *
 * The process of restoring data from a backup takes time.
 * You can check whether your restoration has completed successfully with List Restorations.
 */
export function restoreBackup(payload: object): RequestOptionsFactory<any> {
  function __restoreBackup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v2.backup',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.dstore.api.backup.v2.BackupService.RestoreBackup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressDstoreApiBackupV2BackupServiceUrl({
        protoPath: '/v2/backups/{backupId}/restore',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'restoration.requestedDate' },
              { path: 'restoration.startedDate' },
              { path: 'restoration.finishedDate' },
              { path: 'restoration.backup.requestedDate' },
              { path: 'restoration.backup.startedDate' },
              { path: 'restoration.backup.finishedDate' },
              { path: 'restoration.backup.deletedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __restoreBackup;
}

/**
 * Restores specific collections from a backup.
 *
 * The process of restoring data from a backup takes time.
 * You can check whether your restoration has completed successfully with List Restorations.
 */
export function restorePartialBackup(
  payload: object
): RequestOptionsFactory<any> {
  function __restorePartialBackup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v2.backup',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.dstore.api.backup.v2.BackupService.RestorePartialBackup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressDstoreApiBackupV2BackupServiceUrl({
        protoPath: '/v2/backups/{backupId}/partial-restore',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'restoration.requestedDate' },
              { path: 'restoration.startedDate' },
              { path: 'restoration.finishedDate' },
              { path: 'restoration.backup.requestedDate' },
              { path: 'restoration.backup.startedDate' },
              { path: 'restoration.backup.finishedDate' },
              { path: 'restoration.backup.deletedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __restorePartialBackup;
}

/**
 * Retrieves a list of all data restorations from backups.
 *
 * Results are sorted by requested date, with the newest first.
 *
 * You can use this method to check whether a restoration initiated with Restore Backup has been completed successfully.
 */
export function listRestorations(payload: object): RequestOptionsFactory<any> {
  function __listRestorations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v2.backup',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.dstore.api.backup.v2.BackupService.ListRestorations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressDstoreApiBackupV2BackupServiceUrl({
        protoPath: '/v2/backups/restorations',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'restorations.requestedDate' },
              { path: 'restorations.startedDate' },
              { path: 'restorations.finishedDate' },
              { path: 'restorations.backup.requestedDate' },
              { path: 'restorations.backup.startedDate' },
              { path: 'restorations.backup.finishedDate' },
              { path: 'restorations.backup.deletedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listRestorations;
}

/**
 * Deletes a backup.
 *
 * The process of deleting a backup takes time.
 * You can check whether a backup has been deleted successfully with List Backups.
 */
export function deleteBackup(payload: object): RequestOptionsFactory<any> {
  function __deleteBackup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.v2.backup',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.dstore.api.backup.v2.BackupService.DeleteBackup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressDstoreApiBackupV2BackupServiceUrl({
        protoPath: '/v2/backups/{backupId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBackup;
}
